import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

import { Receipt, ReceiptInsert, ReceiptSearchCommand } from '../../models/receipt/receipt';
import { S3Object } from '../../models/invoice/invoice';
import { ApiResponse, PaginatedApiResponse } from '@card-software/nexus-utilities';

@Injectable({
  providedIn: 'root',
})
export class ReceiptProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.orderApi + '/api/v1/receipt-order';

  public getReceipts(): Observable<ApiResponse<Receipt[]>> {
    return this.httpClient.get<ApiResponse<Receipt[]>>(`${this.baseUrl}/all`);
  }

  public getReceipt(id: string): Observable<ApiResponse<Receipt>> {
    return this.httpClient.get<ApiResponse<Receipt>>(`${this.baseUrl}/${id}`);
  }

  public createReceipt(data: ReceiptInsert): Observable<ApiResponse<Receipt>> {
    return this.httpClient.post<ApiResponse<Receipt>>(this.baseUrl, data);
  }

  public updateReceipt(id: string, data: ReceiptInsert): Observable<ApiResponse<Receipt>> {
    return this.httpClient.put<ApiResponse<Receipt>>(`${this.baseUrl}/${id}`, data);
  }

  public deleteReceipt(id: string): Observable<ApiResponse<unknown>> {
    return this.httpClient.delete<ApiResponse<unknown>>(`${this.baseUrl}/${id}`);
  }

  public searchReceipt(
    receiptSearchCommand: ReceiptSearchCommand,
    pageNumber = 1,
    pageSize = 25,
    matchAny = false,
    sortField?: string,
    sortOrder?: number,
  ): Observable<ApiResponse<PaginatedApiResponse<Receipt>>> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString())
      .set('matchAny', matchAny.toString());
    if (sortField) params = params.set('sortField', sortField);
    if (sortOrder !== undefined) params = params.set('sortOrder', sortOrder.toString());
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<Receipt>>>(`${this.baseUrl}/search`, receiptSearchCommand, { params });
  }

  public uploadFiles(files: File[], receiptId: string): Observable<S3Object[]> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append(`files`, file, file.name);
    });

    return this.httpClient
      .post<{ success: boolean; data: S3Object[]; MessageError: string }>(`${this.baseUrl}/upload-files/${receiptId}`, formData)
      .pipe(map(({ data }) => data));
  }

  public deleteFile(receiptId: string, file: S3Object): Observable<ApiResponse<unknown>> {
    return this.httpClient.post<ApiResponse<unknown>>(`${this.baseUrl}/delete-files/${receiptId}`, [file]);
  }
}
